// Phase Rend (owner 2026-11-02: 'Aren blinks from foe to foe, slashing each and leaving fading afterimages, then snaps back
// to where he started'). Render-only: reads sim.phaseRend / sim.phaseCuts and the phaseStart / phaseBlink / phaseEnd /
// phaseEcho events; decides nothing about gameplay. The hero body itself is drawn at sim.phaseRend.(bx, bz) by view.ts.
//  - afterimages: pooled translucent copies of Aren (violet spectral shell, frozen mid-blow) left where he blinked from;
//    they flicker, then break up into motes as they fade
//  - blink tears: a white-hot streak from the old spot to the new one, gone in ~0.15 s
//  - slashes: a crisp crescent (white core, violet sheath) drawn through each foe along the blink, sweeping in, then erasing
//  - cut lines: the thin glowing cut that hangs in the air (rank 2+), shimmering, with a violet light pool on the ground
//  - the snap back: a tear back to the hero and a tight violet ring at his feet
// Everything pooled; no per-frame allocations.
import * as THREE from 'three/webgpu';
import { abs, color, dot, float, mix, mx_noise_float, normalView, oneMinus, positionViewDirection, positionWorld, pow, smoothstep, time, uniform } from 'three/tsl';
import type { Sim, SimEvent } from '../core/sim';
import type { Fx } from './fx';
import { FLASH } from './fx';
import type { GroundGlow } from './groundGlow';
import { GltfHero } from './heroGltf';
import { RibbonStrips, RS_STRIDE } from './ribbonStrips';
import type { RenderStyle } from './toon';

const VIOLET = 0x8a5cff, VIOLET_EVO = 0xc45cff, PALE = 0xe2d4ff;
const GHOSTS = 6, GHOST_LIFE = 0.55;
const SLASH_POOL = 24, SLASH_SWEEP = 0.05, SLASH_LIFE = 0.42;
const TEAR_LIFE = 0.16, TEAR_POOL = 16;
const Y_SLASH = 1.05, Y_CUT = 0.8, Y_TEAR = 1.15;

type Ev<T extends SimEvent['t']> = Extract<SimEvent, { t: T }>;
interface Slash { on: boolean; x: number; y: number; z: number; dx: number; dz: number; len: number; side: number; tilt: number; t: number; evo: boolean; scale: number }
interface Tear { on: boolean; ax: number; az: number; bx: number; bz: number; t: number }
interface Ghost { g: GltfHero; a: ReturnType<typeof uniform>; t: number }

const _edge = new THREE.Color(), _core = new THREE.Color(), _v = new THREE.Color(VIOLET), _ve = new THREE.Color(VIOLET_EVO), _white = new THREE.Color(1, 1, 1);

export class PhaseRendFx {
  /** world yaw the hero body faces while phasing (view.ts snaps the body to it) */
  yaw = 0;
  /** QA counters */
  blinks = 0; echoes = 0;
  /** QA: freeze slashes / tears / ghosts at this age (s) for captures; null = run normally */
  hold: number | null = null;
  private ribbons: RibbonStrips;
  private slashes: Slash[] = []; private tears: Tear[] = [];
  private ghosts: Ghost[] = []; private ghostsLoading = false; private ghostNext = 0; private strikeSeq = 0;

  constructor(private scene: THREE.Scene, private style: RenderStyle, private file: () => string, private groundAt: (x: number, z: number) => number) {
    this.ribbons = new RibbonStrips(scene, SLASH_POOL + TEAR_POOL + 56, 20, false, 6);
    for (let i = 0; i < SLASH_POOL; i++) this.slashes.push({ on: false, x: 0, y: 0, z: 0, dx: 1, dz: 0, len: 1, side: 1, tilt: 1, t: 9, evo: false, scale: 1 });
    for (let i = 0; i < TEAR_POOL; i++) this.tears.push({ on: false, ax: 0, az: 0, bx: 0, bz: 0, t: 9 });
  }

  /** load the afterimage pool (copies of the hero model); called once the skill is owned so the first cast has them */
  preload(): void {
    if (this.ghosts.length || this.ghostsLoading) return;
    this.ghostsLoading = true;
    const url = `${import.meta.env.BASE_URL}models/${this.file()}`;
    void Promise.all(Array.from({ length: GHOSTS }, async () => {
      const g = new GltfHero(this.style); await g.load(url); g.makeGhost();
      // violet phase shell: bright fresnel rim, scan tears, and a noise dissolve that eats the body as it fades
      const a = uniform(0), m = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false, side: THREE.FrontSide, fog: false });
      const fres = pow(oneMinus(abs(dot(normalView, positionViewDirection))), float(1.6));
      const scan = positionWorld.y.mul(11).add(time.mul(-9)).sin().mul(0.5).add(0.5).pow(8);
      const n = mx_noise_float(positionWorld.mul(4.5)).mul(0.5).add(0.5);
      const keep = smoothstep(oneMinus(a).mul(1.1).sub(0.05), oneMinus(a).mul(1.1).add(0.08), n); // dissolve threshold rises as a falls
      m.colorNode = mix(color(0x6a3cff), color(0xf6f0ff), fres.pow(0.8)).mul(float(1.35).add(scan.mul(0.8)));
      m.opacityNode = fres.mul(0.6).add(0.45).add(scan.mul(0.25)).min(0.97).mul(keep).mul(a.mul(1.4).min(1));
      g.root.traverse((o) => { const mm = o as THREE.Mesh; if (mm.isMesh && mm.visible) { mm.material = m; mm.renderOrder = 5; } });
      g.root.visible = false; this.scene.add(g.root);
      return { g, a, t: 9 } as Ghost;
    })).then((gs) => { this.ghosts = gs; }).catch(() => { this.ghostsLoading = false; });
  }

  event(ev: SimEvent, sim: Sim, fx: Fx, glow: GroundGlow): void {
    switch (ev.t) {
      case 'phaseStart': {
        this.preload();
        const gy = this.groundAt(ev.x, ev.z);
        fx.rings.add(ev.x, gy + 0.12, ev.z, 1.6, 0.22, ev.evolved ? VIOLET_EVO : VIOLET, 2.4, 1);
        for (let k = 0; k < 10; k++) { const a = Math.random() * 6.283; fx.particles.emitDir(ev.x + Math.cos(a) * 0.4, gy + 0.3 + Math.random() * 1.5, ev.z + Math.sin(a) * 0.4, Math.cos(a) * 2, 1.5, Math.sin(a) * 2, k % 2 ? PALE : VIOLET, 0.16, 0.3, 2.6); }
        break;
      }
      case 'phaseBlink': this.blink(ev, sim, fx, glow); break;
      case 'phaseEnd': {
        this.tear(ev.fx, ev.fz, ev.x, ev.z);
        this.ghost(ev.fx, ev.fz, this.yaw, sim);
        const gy = this.groundAt(ev.x, ev.z);
        fx.rings.add(ev.x, gy + 0.12, ev.z, 1.3, 0.2, VIOLET, 2.6, 1);
        fx.stars.add(ev.x, gy + 1.1, ev.z, 1.4, 0.1, PALE, 2.6);
        break;
      }
      case 'phaseEcho': { // Fractured Instant: the afterimage slashes again, an X of two crescents
        this.echoes++;
        const a = Math.random() * Math.PI, c = Math.cos(a), s = Math.sin(a), gy = this.groundAt(ev.x, ev.z);
        this.slash(ev.x, gy + Y_SLASH, ev.z, c, s, ev.r * 1.6, 1, true, 0.8);
        this.slash(ev.x, gy + Y_SLASH, ev.z, -s, c, ev.r * 1.6, -1, true, 0.8);
        fx.stars.add(ev.x, gy + 1, ev.z, ev.r * 0.9, 0.12, PALE, 2.4);
        glow.flash(ev.x, ev.z, ev.r * 1.1, VIOLET_EVO, 0.6, 0.25);
        break;
      }
    }
  }

  private blink(ev: Ev<'phaseBlink'>, sim: Sim, fx: Fx, glow: GroundGlow): void {
    this.blinks++;
    this.yaw = Math.atan2(ev.dx, ev.dz);
    // afterimage where he was (the hero's own spot for the first blink, else the previous blink spot)
    this.ghost(ev.fx, ev.fz, Math.atan2(ev.tx - ev.fx, ev.tz - ev.fz), sim);
    this.tear(ev.fx, ev.fz, ev.x, ev.z);
    const gy = this.groundAt(ev.tx, ev.tz), side = ev.k % 2 ? -1 : 1;
    this.slash(ev.tx, gy + Y_SLASH, ev.tz, ev.dx, ev.dz, ev.len, side, ev.evolved, 1);
    // a white star where the blade meets the foe, violet sparks thrown along the cut
    fx.stars.add(ev.tx, gy + 1.1, ev.tz, 1.5, 0.09, 0xffffff, 2.6);
    fx.stars.add(ev.tx, gy + 1.1, ev.tz, 2.4, 0.14, ev.evolved ? VIOLET_EVO : VIOLET, 1.6);
    for (let k = 0; k < 8; k++) {
      const sp = 5 + Math.random() * 7, j = (Math.random() - 0.5) * 4;
      fx.particles.emitDir(ev.tx, gy + 0.9 + Math.random() * 0.5, ev.tz, ev.dx * sp - ev.dz * j, 1 + Math.random() * 2.5, ev.dz * sp + ev.dx * j, k % 3 ? PALE : 0xffffff, 0.14, 0.22, 2.6);
    }
    glow.flash(ev.tx, ev.tz, 1.6, ev.evolved ? VIOLET_EVO : VIOLET, 0.7, 0.2);
    // arrival: a tight violet ring under the body and motes peeling off it
    const by = this.groundAt(ev.x, ev.z);
    fx.rings.add(ev.x, by + 0.1, ev.z, 1.2, 0.18, ev.evolved ? VIOLET_EVO : VIOLET, 2.6, 1);
    for (let k = 0; k < 6; k++) { const a = Math.random() * 6.283; fx.particles.emitDir(ev.x + Math.cos(a) * 0.35, by + 0.4 + Math.random() * 1.4, ev.z + Math.sin(a) * 0.35, Math.cos(a) * 1.5 - ev.dx * 2, 0.8, Math.sin(a) * 1.5 - ev.dz * 2, k % 2 ? PALE : VIOLET, 0.13, 0.3, 2.6); }
    if (FLASH.dim >= 1 && ev.k % 2 === 0) fx.lights.flash(ev.tx, gy + 1, ev.tz, VIOLET, 10, 0.12, 6);
    fx.shake = Math.min(1, fx.shake + 0.06);
  }

  private slash(x: number, y: number, z: number, dx: number, dz: number, len: number, side: number, evo: boolean, scale: number): void {
    const s = this.slashes.reduce((a, b) => (!b.on ? (a.on ? b : a) : !a.on ? a : b.t > a.t ? b : a));
    Object.assign(s, { on: true, x, y, z, dx, dz, len, side, tilt: Math.random() < 0.5 ? -1 : 1, t: 0, evo, scale });
  }
  private tear(ax: number, az: number, bx: number, bz: number): void {
    const t = this.tears.reduce((a, b) => (!b.on ? (a.on ? b : a) : !a.on ? a : b.t > a.t ? b : a));
    Object.assign(t, { on: true, ax, az, bx, bz, t: 0 });
  }
  private ghost(x: number, z: number, yaw: number, sim: Sim): void {
    if (!this.ghosts.length) { // not loaded yet: a column of violet motes stands in
      const gy = this.groundAt(x, z);
      for (let k = 0; k < 10; k++) this.ribbonMote(x, gy, z, k);
      return;
    }
    const gh = this.ghosts[this.ghostNext++ % this.ghosts.length];
    gh.t = 0; gh.a.value = 1;
    const g = gh.g; g.root.visible = true; g.root.position.set(x, this.groundAt(x, z), z); g.root.rotation.set(0, yaw, 0);
    // freeze it mid-blow: kick off the next strike of the approved Rush Flurry combo and advance into it, then stop updating
    const d = { speed: 0, firing: false, charging: false, transformed: false, hurt: false, dead: false, faceYaw: yaw, moveYaw: yaw, turn: 0, surgeTier: 0.9, strike: ++this.strikeSeq, strikeYaw: yaw };
    g.update(0.001, d); g.update(0.06, d);
    void sim;
  }
  private moteQueue: number[] = [];
  private ribbonMote(x: number, gy: number, z: number, k: number): void { this.moteQueue.push(x, gy + 0.2 + k * 0.17, z); }

  update(dt: number, sim: Sim, cam: THREE.Camera, fx: Fx): void {
    if (sim.weapons.has('phase')) this.preload();
    const camPos = cam.position, R = this.ribbons, q = R.pts;
    R.begin();
    // queued fallback motes (before the ghosts load)
    for (let i = 0; i + 2 < this.moteQueue.length; i += 3) fx.particles.emitDir(this.moteQueue[i], this.moteQueue[i + 1], this.moteQueue[i + 2], 0, 0.3, 0, VIOLET, 0.32, 0.45, 2.4);
    this.moteQueue.length = 0;
    // slashes: a crescent through the foe, the head sweeping along it, then the tail erasing toward the tip
    for (const s of this.slashes) {
      if (!s.on) continue;
      s.t = this.hold ?? s.t + dt;
      if (s.t > SLASH_LIFE) { s.on = false; continue; }
      const head = Math.min(1, s.t / SLASH_SWEEP), tail = Math.max(0, (s.t - SLASH_SWEEP * 1.2) / (SLASH_LIFE - SLASH_SWEEP * 1.2));
      const L = s.len * 1.55 * s.scale, px = -s.dz * s.side, pz = s.dx * s.side, N = R.seg + 1;
      for (let i = 0; i < N; i++) {
        const u = i / (N - 1), uu = Math.min(u, head);
        const along = (uu - 0.5) * L, bulge = Math.sin(Math.PI * uu) * L * 0.26, up = (uu - 0.5) * L * 0.3 * s.tilt;
        const o = i * RS_STRIDE;
        q[o] = s.x + s.dx * along + px * bulge; q[o + 1] = s.y + up; q[o + 2] = s.z + s.dz * along + pz * bulge;
        const prof = Math.pow(Math.sin(Math.PI * Math.min(1, Math.max(0, u))), 0.55);
        const erased = u < tail ? 0 : Math.min(1, (u - tail) * 6);
        q[o + 3] = (u <= head ? 1 : 0) * prof * 0.95 * s.scale * (0.3 + 0.7 * erased);
        q[o + 4] = erased;
      }
      const hot = 1 - Math.min(1, s.t / 0.12);
      _edge.copy(s.evo ? _ve : _v).multiplyScalar(1.5 + hot);
      _core.copy(_white).multiplyScalar(1.5 + hot);
      R.commit(N, camPos, _edge, _core, 0.42);
    }
    // blink tears: wide and white at the arrival end, thin at the departure end
    for (const t of this.tears) {
      if (!t.on) continue;
      t.t = this.hold ?? t.t + dt;
      if (t.t > TEAR_LIFE) { t.on = false; continue; }
      const f = 1 - t.t / TEAR_LIFE, N = 8, ga = this.groundAt(t.ax, t.az), gb = this.groundAt(t.bx, t.bz);
      for (let i = 0; i < N; i++) {
        const u = i / (N - 1), o = i * RS_STRIDE;
        q[o] = t.ax + (t.bx - t.ax) * u; q[o + 1] = ga + (gb - ga) * u + Y_TEAR; q[o + 2] = t.az + (t.bz - t.az) * u;
        q[o + 3] = (0.06 + 0.5 * u) * f; q[o + 4] = Math.min(1, f * 1.5) * (0.3 + 0.7 * u);
      }
      _edge.copy(_v).multiplyScalar(1.4); _core.copy(_white).multiplyScalar(1.6);
      R.commit(N, camPos, _edge, _core, 0.6);
      if (t.t < dt * 1.5) for (let k = 0; k < 6; k++) { const u = Math.random(); fx.particles.emitDir(t.ax + (t.bx - t.ax) * u, ga + Y_TEAR + (Math.random() - 0.5) * 0.8, t.az + (t.bz - t.az) * u, (t.bx - t.ax) * 2, 0.4, (t.bz - t.az) * 2, k % 2 ? PALE : VIOLET, 0.12, 0.25, 2.4); }
    }
    // cut lines (sim state): a thin shimmering cut hanging in the air, fading over its last third
    for (const c of sim.phaseCuts) {
      const k = c.t / c.life, a = Math.min(1, (1 - k) * 3), N = 10, g0 = this.groundAt(c.x0, c.z0), g1 = this.groundAt(c.x1, c.z1);
      const fl = 0.85 + 0.15 * Math.sin(c.t * 40 + c.id);
      for (let pass = 0; pass < 2; pass++) { // a soft violet halo, then the crisp white-cored cut on top
        for (let i = 0; i < N; i++) {
          const u = i / (N - 1), o = i * RS_STRIDE, jit = Math.sin(u * 23 + c.t * 31 + c.id) * 0.02;
          q[o] = c.x0 + (c.x1 - c.x0) * u; q[o + 1] = g0 + (g1 - g0) * u + Y_CUT + jit; q[o + 2] = c.z0 + (c.z1 - c.z0) * u;
          q[o + 3] = (pass ? 0.2 : 0.62) * Math.pow(Math.sin(Math.PI * u), 0.4) * fl; q[o + 4] = a * (pass ? 1 : 0.32);
        }
        if (pass) { _edge.copy(c.evo ? _ve : _v).multiplyScalar(1.7); _core.copy(_white).multiplyScalar(1.8); } else { _edge.copy(c.evo ? _ve : _v).multiplyScalar(1.2); _core.copy(_edge); }
        R.commit(N, camPos, _edge, _core, pass ? 0.5 : 0);
      }
      if (Math.random() < 0.25 * a) { const u = Math.random(); fx.particles.emitDir(c.x0 + (c.x1 - c.x0) * u, g0 + Y_CUT, c.z0 + (c.z1 - c.z0) * u, 0, 0.6 + Math.random(), 0, Math.random() < 0.5 ? PALE : VIOLET, 0.1, 0.35, 2.6); }
    }
    R.end();
    // afterimages fade and dissolve
    for (const gh of this.ghosts) {
      if (!gh.g.root.visible) continue;
      gh.t = this.hold ?? gh.t + dt;
      const a = gh.t < GHOST_LIFE ? Math.pow(1 - gh.t / GHOST_LIFE, 0.8) : 0;
      gh.a.value = a;
      if (a <= 0.01) { gh.g.root.visible = false; continue; }
      if (Math.random() < 0.4 * a) { const p = gh.g.root.position; fx.particles.emitDir(p.x + (Math.random() - 0.5) * 0.6, p.y + 0.3 + Math.random() * 1.6, p.z + (Math.random() - 0.5) * 0.6, 0, 0.8, 0, Math.random() < 0.5 ? PALE : VIOLET, 0.12, 0.4, 2.4); }
    }
  }

  /** violet light pools: under every live cut line */
  glowPools(g: GroundGlow, sim: Sim): void {
    for (const c of sim.phaseCuts) {
      const a = Math.min(1, (1 - c.t / c.life) * 3) * 0.55;
      g.strip(c.x0, c.z0, c.x1, c.z1, this.groundAt(c.x0, c.z0) + Y_CUT, 0.45, c.evo ? VIOLET_EVO : VIOLET, a, 1, 0.6);
    }
  }

  clear(): void { for (const s of this.slashes) s.on = false; for (const t of this.tears) t.on = false; for (const g of this.ghosts) g.g.root.visible = false; this.ribbons.clear(); this.moteQueue.length = 0; }
}
